import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { getBlueprintsCatalog, getPrintProviders } from '@/api/storesApp/printify';
import { Blueprint } from '@/typings/printify/blueprint';
import { PrintProviderClass, SelectedProviderVariants } from '@/typings/printify/printProvider';
import { Placeholder } from '../VariantsConfigurator/typings';
import type { GenericPodProduct } from '../ProductSelector/typings';
import type {
    PodProviderConfiguration,
    TemplateProductConfiguration
} from '../typings';
import ProductSelector from '../ProductSelector/ProductSelector.vue';
import MultiSelectTable from '@/components/MultiSelectTable/MultiSelectTable.vue';
import VariantsConfigurator from '@/components/TemplateProductPodAssigner/VariantsConfigurator/VariantsConfigurator.vue'
import RevisionStep from '@/components/TemplateProductPodAssigner/RevisionStep/RevisionStep.vue'
import { formatAspectRatio } from '@/utils/formatUtils';

@Component({
    name: 'PrintifyConfigurator',
    components: {
        ProductSelector,
        MultiSelectTable,
        VariantsConfigurator,
        RevisionStep
    }
})
export default class PrintifyConfigurator extends Vue {
    @Prop({ required: true }) store!: string;
    @Prop({ required: true }) initialConfiguration!: TemplateProductConfiguration;

    showUnassignConfirmModal = true;
    productToUnassign: GenericPodProduct | null = null;

    printifyProducts: Blueprint[] = [];
    products: GenericPodProduct[] = [];
    selectedProducts: GenericPodProduct[] = [];
    selectedProduct: GenericPodProduct | null = null;
    printProviders: PrintProviderClass[] | null = null;
    selectedPrintProviders: PrintProviderClass[] = [];
    selectedProviderVariants: SelectedProviderVariants[] = [];

    step: 'selectProduct' | 'selectPrintProviders' | 'selectPlaceholders' | 'finalRevision' = 'selectProduct';
    templateProductConfiguration: TemplateProductConfiguration = this.initialConfiguration;
    biggestPlaceholders: Placeholder[] = [];

    readonly printProvidersTableFields = [{ key: 'name', label: 'Print Provider' }];

    formatAspectRatio(placeholder: Placeholder): string {
        return formatAspectRatio(placeholder.width, placeholder.height);
    }

    getPrintProviderName(providerId: string | number): string {
        if (this.printProviders) {
            return this.printProviders.find((p) => p.id.toString() === providerId)?.title || '';
        }
        return '';
    }

    async loadProducts() {
        this.printifyProducts = await getBlueprintsCatalog(this.store);
        this.products = this.printifyProducts.map((p) => {
            return {
                id: p.id.toString(),
                title: p.title,
                image: p.images[0],
                description: p.description
            };
        });

        const ids = this.templateProductConfiguration.configurations.map((c) => c.productId);
        this.selectedProducts = this.products.filter((p) => ids.includes(p.id));
    }

    async onProductSelected(product: GenericPodProduct) {
        this.selectedProduct = product;
        this.printProviders = null;
        this.step = 'selectPrintProviders';
        this.printProviders = await getPrintProviders(Number(this.selectedProduct.id), this.store);

        // Wait to the next tick so elements in the table are populated and we can select them
        await this.$nextTick();

        if (this.printProviders.length) {
            // Select any print providers that are already configured
            const configuredPrintProviders = this.printProviders.filter((printProvider) => {
                return this.templateProductConfiguration.configurations
                    .find((c) => c.productId === product.id)
                    ?.placeholders.some((placeholder) => placeholder.printProviderId === printProvider.id.toString());
            });

            if (configuredPrintProviders.length) {
                this.selectedPrintProviders = configuredPrintProviders;
            } else {
                // By default select the first print provider
                this.selectedPrintProviders = [this.printProviders[0]];
            }
        }
    }

    onProductUnassignClick(product: GenericPodProduct) {
        this.productToUnassign = product;
        this.showUnassignConfirmModal = true;
    }

    unassignProduct(product: GenericPodProduct | null) {
        if (product) {
            // Remove any POD configuration belonging to the unassigned product
            this.templateProductConfiguration.configurations = this.templateProductConfiguration.configurations.filter(
                (c) => c.productId !== product.id
            );
            this.onSave();
        }
    }

    async onPrintProvidersSelected() {
        this.step = 'selectPlaceholders';
    }

    onPlaceholdersSelectionFinished(value: {
            configurations: PodProviderConfiguration[], 
            biggestPlaceholders: Placeholder[], 
            selectedProviderVariants: SelectedProviderVariants[]
        }) {
        this.step = 'finalRevision';
        this.templateProductConfiguration.configurations = value.configurations;
        this.biggestPlaceholders = value.biggestPlaceholders;
        this.selectedProviderVariants = value.selectedProviderVariants.map(prov => {
            return {
                ...prov,
                printProviderName: this.getPrintProviderName(prov.printProviderId),
            };
        });
    }

    onSave() {
        const result = this.biggestPlaceholders.map((x) => {
            return {
                width: x.width,
                height: x.height,
                id: x.position,
                aspectRatio: this.formatAspectRatio(x)
            };
        });
        this.$emit('save', {
            configuration: this.templateProductConfiguration,
            biggestPlaceholders: result
        });
    }

    async created() {
        await this.loadProducts();
    }
}
